import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";

/**
 * A component that conditionally renders either a web or webview version of component
 * based on the platform context.
 *
 * This adaptor is useful when you need to render different UI components depending on
 * whether the app is running in a React Native webview or web browser.
 *
 * @example
 * ```tsx
 * <WebviewComponentAdaptor
 *   webComponent={<WebButton onClick={handleClick} />}
 *   webviewComponent={<MobileButton onPress={handleClick} />}
 * />
 * ```
 *
 * @param webComponent - The JSX element to render in a web browser
 * @param webviewComponent - The JSX element to render in a React Native webview
 * @returns The appropriate component based on the platform context
 */
export default function WebviewComponentAdaptor({
  webComponent,
  webviewComponent,
}: {
  webComponent: JSX.Element | null;
  webviewComponent: JSX.Element | null;
}): JSX.Element | null {
  const { webviewValueAdapter } = useMobileWebviewContext();

  return (
    webviewValueAdapter({ web: webComponent, webview: webviewComponent }) ||
    null
  );
}
